import { useState } from 'react';
import { Panel } from './Page';
import { Chip } from './Chips';
import { EmptyState, ErrorState, TableSkeleton } from './Feedback';
import { TableWrap } from './Table';
import { useAuth } from '../lib/auth';
import { licencePurchases } from '../lib/endpoints';
import { useAsync } from '../lib/hooks';
import { useOnboarding } from '../lib/onboarding';
import { useToast } from '../lib/toast';
import { errorMessage } from '../lib/api';
import { formatDateTime, formatMoney, formatRelative } from '../lib/format';
import type { LicensePurchaseDto } from '../lib/types';

function paymentTone(purchase: LicensePurchaseDto): 'good' | 'warn' | 'bad' | 'neutral' {
  if (purchase.paidUtc) return 'good';
  const status = (purchase.paymentStatus ?? '').toUpperCase();
  if (status.includes('ERROR') || status.includes('FAIL') || status.includes('CANCEL')) return 'bad';
  if (status) return 'warn';
  return 'neutral';
}

/**
 * THE PURCHASE LEDGER. Every licence purchase this company created, with what the Registration
 * Server has since said about it. Business Central keeps this list on its licence intake page;
 * without it a checkout interrupted by a hosting timeout had no way back to its payment link, and
 * nobody could see whether an order was paid without opening Stripe.
 *
 * Status arrives on its own (the Registration Server reports payment changes as platform
 * deliveries); Refresh asks now. Open payment link re-creates the checkout for an unpaid purchase
 * under the same purchase id, so it can never order twice.
 */
export function LicencePurchasesPanel(): JSX.Element {
  const toast = useToast();
  const { can } = useAuth();
  const canManage = can('connection.manage');
  const [busyId, setBusyId] = useState<string | null>(null);
  const onboarding = useOnboarding();

  const list = useAsync((signal) => licencePurchases.list(signal), []);

  const refresh = async (purchase: LicensePurchaseDto) => {
    setBusyId(purchase.licensePurchaseId);
    try {
      const updated = await licencePurchases.refresh(purchase.licensePurchaseId);
      toast.success('Purchase status refreshed from iERP.');
      list.reload();
      // A paid purchase can move the licence itself (a renewal extends it). The server has just
      // re-read the licence; reload the plan panel so it stops showing the old expiry.
      if (updated.paidUtc || (updated.status ?? '').replace(/\s/g, '').toUpperCase() === 'LICENSEACTIVATED') {
        onboarding.reload();
      }
    } catch (caught) {
      toast.error('Could not refresh the purchase', errorMessage(caught));
    } finally {
      setBusyId(null);
    }
  };

  const openPaymentLink = async (purchase: LicensePurchaseDto) => {
    setBusyId(purchase.licensePurchaseId);
    try {
      const updated = await licencePurchases.paymentLink(purchase.licensePurchaseId);
      if (updated.stripePaymentUrl) {
        window.location.assign(updated.stripePaymentUrl);
        return;
      }
      toast.warn('No payment link yet', updated.lastMessage ?? 'iERP did not return a checkout link. Try again in a moment.');
      list.reload();
    } catch (caught) {
      toast.error('Could not open the payment link', errorMessage(caught));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Panel
      title="Licence purchases"
      description="What this company ordered, whether it is paid, and the payment link. Status changes arrive from iERP on their own."
    >
      {list.loading && !list.data ? (
        <TableSkeleton rows={3} columns={6} />
      ) : list.error ? (
        <ErrorState message="Purchases could not be read." detail={list.error} onRetry={list.reload} />
      ) : !list.data || list.data.length === 0 ? (
        <EmptyState
          title="No purchases yet"
          description="A purchase appears here the moment checkout is started from Plans and licences, and stays here with its payment status."
        />
      ) : (
        <TableWrap busy={list.loading}>
          <thead>
            <tr>
              <th scope="col">Purchase</th>
              <th scope="col">Plan</th>
              <th scope="col">Amount</th>
              <th scope="col">Payment</th>
              <th scope="col">Documents</th>
              <th scope="col">Created</th>
              <th scope="col" className="col-actions">
                <span className="visually-hidden">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {list.data.map((p) => (
              <tr key={p.id}>
                <td>
                  <span className="cell-strong mono">{p.licensePurchaseId}</span>
                  <span className="cell-sub">{p.status ?? '—'} · via {p.source.toLowerCase()}</span>
                </td>
                <td>
                  {p.packageCode ?? p.basePackageCode ?? '—'}
                  <span className="cell-sub">{p.billingPeriod ?? ''}</span>
                </td>
                <td>
                  {formatMoney(p.amountToPay ?? p.amountIncludingTax ?? p.lineAmount, p.currencyCode)}
                </td>
                <td>
                  <Chip tone={paymentTone(p)}>{p.paidUtc ? 'Paid' : p.paymentStatus ?? 'Unknown'}</Chip>
                  <span className="cell-sub">
                    {p.paidUtc ? `paid ${formatRelative(p.paidUtc)}` : p.paymentMethod ?? ''}
                  </span>
                  {p.lastMessage ? <span className="cell-sub">{p.lastMessage}</span> : null}
                </td>
                <td>
                  {p.postedSalesInvoiceNo ?? p.salesInvoiceNo ?? p.salesOrderNo ?? '—'}
                  <span className="cell-sub">
                    {p.salesOrderNo && p.postedSalesInvoiceNo ? `order ${p.salesOrderNo}` : ''}
                  </span>
                </td>
                <td>
                  {formatDateTime(p.createdUtc)}
                  <span className="cell-sub">{p.createdByEmail ?? ''}</span>
                </td>
                <td className="col-actions">
                  {canManage ? (
                    <>
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        disabled={busyId !== null}
                        onClick={() => void refresh(p)}
                      >
                        Refresh
                      </button>
                      {!p.paidUtc ? (
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          disabled={busyId !== null}
                          onClick={() => void openPaymentLink(p)}
                        >
                          Open payment link
                        </button>
                      ) : null}
                    </>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </TableWrap>
      )}
    </Panel>
  );
}
